import { collection, onSnapshot, query, where } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { POST_ANALYTICS_COLLECTION, PostAnalytics } from "../models/PostAnalytics";
import { db } from "../services/Firebase";

/**
 * Live engagement analytics for a page of posts (Posts list view). Rows are
 * always visible — no lazy trigger like the calendar peek's hover — so this
 * batches ids into `in` queries of 30 (Firestore's limit) instead of opening
 * one subscription per row. Returns wavegenPostId → records (one per
 * platform connection the post published to).
 */
export function usePostAnalyticsForPosts(
  postIds: string[]
): Record<string, PostAnalytics[]> {
  const { currentUser, activeWorkspaceId } = useAuth();
  const [analyticsState, setAnalyticsState] = useState<{
    workspaceId: string | null;
    byPostId: Record<string, PostAnalytics[]>;
  }>({ workspaceId: null, byPostId: {} });
  // Stable key so pagination appends re-subscribe once, not per render.
  const idsKey = useMemo(() => [...postIds].sort().join(","), [postIds]);

  useEffect(() => {
    if (!currentUser || !activeWorkspaceId || idsKey === "") {
      setAnalyticsState({ workspaceId: activeWorkspaceId, byPostId: {} });
      return;
    }
    const ids = idsKey.split(",");
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += 30) chunks.push(ids.slice(i, i + 30));

    const perChunk: Array<Record<string, PostAnalytics[]>> = chunks.map(
      () => ({})
    );
    const unsubscribes = chunks.map((chunk, idx) =>
      onSnapshot(
        query(
          collection(db, POST_ANALYTICS_COLLECTION),
          // userId remains the compatibility tenant alias; its value is the
          // workspace id on both legacy and workspace-aware analytics docs.
          where("userId", "==", activeWorkspaceId),
          where("wavegenPostId", "in", chunk)
        ),
        (snap) => {
          const next: Record<string, PostAnalytics[]> = {};
          for (const d of snap.docs) {
            const data = {
              ...(d.data() as Omit<PostAnalytics, "id">),
              id: d.id,
            } as PostAnalytics;
            if (data.userId !== activeWorkspaceId) continue;
            (next[data.wavegenPostId] = next[data.wavegenPostId] ?? []).push(
              data
            );
          }
          perChunk[idx] = next;
          setAnalyticsState({
            workspaceId: activeWorkspaceId,
            byPostId: Object.assign({}, ...perChunk),
          });
        },
        // Metrics are decoration on this surface; a failed listener just
        // leaves the rows blank.
        () => {}
      )
    );
    return () => unsubscribes.forEach((u) => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, activeWorkspaceId, idsKey]);

  return analyticsState.workspaceId === activeWorkspaceId
    ? analyticsState.byPostId
    : {};
}
